import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';

import BookingsServices from '@/features/BookingsServices';
import CheckoutMain from '@/features/Checkout/CheckoutMain/CheckoutMain';
import CheckoutPayments from '@/features/Checkout/CheckoutPayments/CheckoutPayments';
import CustomerEdit from '@/features/CustomerEdit';
import Customers from '@/features/Customers/index';
import FulfilmentProviders from '@/features/FulfilmentProviders';
import InventoryCreate from '@/features/InventoryPage/InventoryCreate/InventoryCreate';
import InventoryEdit from '@/features/InventoryPage/InventoryEdit/InventoryEdit';
import InventoryMain from '@/features/InventoryPage/InventoryMain/InventoryMain';
import Login from '@/features/Login';
import OrderCreate from '@/features/OrderCreate';
import OrderEdit from '@/features/OrderEdit';
import OrderInvoice from '@/features/OrderInvoice/OrderInvoice';
import Orders from '@/features/Orders';
import PaymentDetails from '@/features/PaymentDetails';
import PaymentProcessors from '@/features/PaymentProcessors';
import Payments from '@/features/Payments';
import ProductAdd from '@/features/Products/ProductAdd';
import ProductEdit from '@/features/Products/ProductEdit';
import ProductsMain from '@/features/Products/ProductsMain/ProductsMain';
import EditShippingProfile from '@/features/Shipping/EditShippingProfile/EditShippingProfile';
import ShippingProfiles from '@/features/Shipping/ShippingProfiles/ShippingProfiles';
import ShippingProviders from '@/features/ShippingProviders/ShippingProviders';
import UserSettings from '@/features/UserSettings';
import UsersManagement from '@/features/UsersManagement';
import AccountSettings from '@/shared/components/layouts/AccountSettings';
import { AppPath, AuthPath } from '@/shared/constants/path';

import ErrorBoundry from '../components/layouts/ErrorBoundry';
import RootLayout from '../components/layouts/Root';

type BrowserRouterProps = ReturnType<typeof createBrowserRouter>;

/**
 *  Router configuration for the main app.
 *  @param basename - The base URL for the app.
 *  @returns The router configuration.
 *
 *  @example <Link to="/" />; // results in <a href="/app" />
 */
const getRouterConfig = (basename: string): BrowserRouterProps => {
  return createBrowserRouter(
    [
      {
        path: AppPath.Root,
        element: (
          <RootLayout>
            <Outlet />
          </RootLayout>
        ),
        errorElement: <ErrorBoundry />,
        children: [
          {
            index: true,
            element: <Navigate to={AppPath.Customers} replace />,
          },
          {
            path: AppPath.Customers,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Customers />,
              },
              {
                path: `${AppPath.Customers}/:id`,
                element: <CustomerEdit />,
              },
            ],
          },
          {
            path: AppPath.Orders,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Orders />,
              },
              {
                path: `${AppPath.Orders}/:id`,
                element: <OrderEdit />,
              },
              {
                path: `${AppPath.Orders}/create`,
                element: <OrderCreate />,
              },
            ],
          },
          {
            path: AppPath.Products,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <ProductsMain />,
              },
              {
                path: `${AppPath.Products}/add`,
                element: <ProductAdd />,
              },
              {
                path: `${AppPath.Products}/:id`,
                element: <ProductEdit />,
              },
            ],
          },
          {
            path: AppPath.Inventory,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <InventoryMain />,
              },
              {
                path: `${AppPath.Inventory}/create`,
                element: <InventoryCreate />,
              },
              {
                path: `${AppPath.Inventory}/:id`,
                element: <InventoryEdit />,
              },
            ],
          },
          {
            path: AppPath.Settings,
            element: <AccountSettings />,
            children: [
              {
                index: true,
                element: (
                  <Navigate to={AppPath.Settings_UserSettings} replace />
                ),
              },
              {
                path: AppPath.Settings_FulfilmentProviders,
                element: <FulfilmentProviders />,
              },
              {
                path: AppPath.Settings_UserSettings,
                element: <UserSettings />,
              },
              {
                path: AppPath.Settings_UsersManagement,
                element: <UsersManagement />,
              },
              {
                path: AppPath.Settings_PaymentProcessors,
                element: <PaymentProcessors />,
              },
              {
                path: AppPath.Settings_ShippingProviders,
                element: <ShippingProviders />,
              },
            ],
          },
          {
            path: AppPath.Bookings,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Navigate to={AppPath.Bookings_Services} replace />,
              },
              {
                path: AppPath.Bookings_Services,
                element: <BookingsServices />,
              },
            ],
          },
          {
            path: AppPath.Payments,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Payments />,
              },
              {
                path: `${AppPath.Payments}/:id`,
                element: <PaymentDetails />,
              },
            ],
          },
          {
            path: AppPath.Checkout,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <CheckoutMain />,
              },
              {
                path: `${AppPath.Checkout}/${AppPath.Payments}/:id`,
                element: <CheckoutPayments />,
              },
            ],
          },
          {
            path: AppPath.Shipping,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <Navigate to={AppPath.Shipping_Profiles} replace />,
              },
              {
                path: AppPath.Shipping_Profiles,
                element: <ShippingProfiles />,
              },
              {
                path: `${AppPath.Shipping_Profiles}/:id`,
                element: <EditShippingProfile />,
              },
            ],
          },
        ],
      },
      {
        path: AuthPath.Login,
        element: <Login />,
      },
      {
        path: AppPath.Invoices,
        element: <Outlet />,
        children: [
          {
            path: `${AppPath.Invoices}${AppPath.Orders}/:id`,
            element: <OrderInvoice />,
          },
        ],
      },
    ],
    { basename },
  );
};

export default getRouterConfig;
